import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Shield, User as UserIcon, Wallet, X, KeyRound, Power } from 'lucide-react';
import { adminApi } from '../lib/api';

const ROLE_LABEL = {
  super_admin: { label: 'Super Admin', color: 'bg-orange-100 text-[#E89534]', Icon: Shield },
  comptable: { label: 'Comptable', color: 'bg-blue-100 text-blue-700', Icon: Wallet },
  guide: { label: 'Guide', color: 'bg-emerald-100 text-emerald-700', Icon: UserIcon }
};

const RoleBadge = ({ role }) => {
  const r = ROLE_LABEL[role] || { label: role, color: 'bg-slate-100 text-slate-700', Icon: UserIcon };
  const Icon = r.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${r.color}`}>
      <Icon className="w-3 h-3" /> {r.label}
    </span>
  );
};

const UsersManagement = ({ currentUsername }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', full_name: '', email: '', role: 'guide' });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminApi.getUsers());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setError('');
    if (!form.username || form.password.length < 8) {
      return setError('Nom d\'utilisateur requis et mot de passe min 8 caractères');
    }
    try {
      await adminApi.createUser(form);
      setForm({ username: '', password: '', full_name: '', email: '', role: 'guide' });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur');
    }
  };

  const toggleActive = async (u) => {
    await adminApi.updateUser(u.id, { active: !u.active });
    load();
  };

  const changeRole = async (u, role) => {
    await adminApi.updateUser(u.id, { role });
    load();
  };

  const remove = async (u) => {
    if (window.confirm(`Supprimer le compte ${u.username} ?`)) {
      try {
        await adminApi.deleteUser(u.id);
        load();
      } catch (err) {
        alert(err?.response?.data?.detail || 'Erreur');
      }
    }
  };

  const doResetPassword = async () => {
    if (resetPwd.length < 8) return;
    await adminApi.updateUser(resetTarget.id, { password: resetPwd });
    setResetTarget(null);
    setResetPwd('');
    alert('Mot de passe réinitialisé');
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Comptes administrateurs</h3>
          <p className="text-sm text-slate-500">Gérez les accès et rôles de votre équipe</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full">
          <Plus className="w-4 h-4" /> Nouveau compte
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border-2 border-[#F4A340]/30 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Nom d'utilisateur *" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            <input type="password" placeholder="Mot de passe (min 8) *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            <input placeholder="Nom complet" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">Rôle</label>
            <div className="grid grid-cols-3 gap-2">
              {['super_admin', 'comptable', 'guide'].map((r) => (
                <button
                  key={r}
                  onClick={() => setForm({ ...form, role: r })}
                  className={`p-3 rounded-xl border-2 transition-all ${form.role === r ? 'border-[#F4A340] bg-orange-50' : 'border-slate-200 hover:border-[#F4A340]/50'}`}
                >
                  <RoleBadge role={r} />
                </button>
              ))}
            </div>
          </div>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100">Annuler</button>
            <button onClick={submit} className="px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full">Créer le compte</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#F4A340]" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  {['Utilisateur', 'Rôle', 'Email', 'Créé le', 'Dernière connexion', 'Statut', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 font-semibold text-slate-600 uppercase text-xs tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const isMe = u.username === currentUsername;
                  return (
                    <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F4A340] to-[#E89534] flex items-center justify-center text-white text-xs font-bold">
                            {u.username.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium">{u.username} {isMe && <span className="text-xs text-slate-400">(vous)</span>}</div>
                            {u.full_name && <div className="text-xs text-slate-500">{u.full_name}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          disabled={isMe}
                          onChange={(e) => changeRole(u, e.target.value)}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1 outline-none focus:border-[#F4A340] disabled:opacity-50"
                        >
                          <option value="super_admin">Super Admin</option>
                          <option value="comptable">Comptable</option>
                          <option value="guide">Guide</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{u.email || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{u.last_login ? new Date(u.last_login).toLocaleString('fr-FR') : 'Jamais'}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleActive(u)} disabled={isMe} className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${u.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'} disabled:opacity-50`}>
                          <Power className="w-3 h-3" /> {u.active ? 'Actif' : 'Inactif'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => setResetTarget(u)} title="Réinitialiser mot de passe" className="p-2 rounded-lg hover:bg-blue-50 text-blue-600">
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(u)} disabled={isMe} title="Supprimer" className="p-2 rounded-lg hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setResetTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Réinitialiser le mot de passe</h3>
              <button onClick={() => setResetTarget(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">Compte : <b>{resetTarget.username}</b></p>
            <input
              type="password"
              autoFocus
              placeholder="Nouveau mot de passe (min 8)"
              value={resetPwd}
              onChange={(e) => setResetPwd(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setResetTarget(null)} className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100">Annuler</button>
              <button onClick={doResetPassword} disabled={resetPwd.length < 8} className="px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full">Réinitialiser</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
